import {
  FormAttributionBoolean,
  FormAttributionElement,
  FormAttributionInteraction,
} from '../types';
import {
  getFieldName,
  getPagePlatform,
  normalizeTrackingValue,
} from '../utils';

import type { FormInteraction } from '../types';

export const createFormInteractionEventFromField = (args: {
  interaction: FormAttributionInteraction;
  formName: string;
  field: HTMLInputElement | HTMLSelectElement;
}): FormInteraction => {
  const interaction = getInteraction(args.interaction, args.field);

  return {
    event_name: 'form',
    form_name: substituteFormName(args.formName),
    form_interaction: `${interaction}.${createInteractionString(args.field)}`,
  };
};

export const substituteFormName = (providedName: string): string => {
  if (providedName.includes('.')) {
    return providedName;
  }

  providedName = normalizeTrackingValue(providedName);

  const platform = getPagePlatform();

  if (typeof platform === 'undefined') {
    return providedName;
  }

  return `${platform}.${providedName}`;
};

const getInteraction = (
  interaction: FormAttributionInteraction,
  field: HTMLInputElement | HTMLSelectElement,
): FormAttributionInteraction => {
  if (interaction !== FormAttributionInteraction.Fill) {
    return interaction;
  }

  // Filled events for checkboxes or radio buttons are considered as click events
  if (field.type === 'checkbox' || field.type === 'radio') {
    return FormAttributionInteraction.Click;
  }

  // Filled events for input fields with an empty value are considered as clear events
  if (field.value.trim() === '') {
    return FormAttributionInteraction.Clear;
  }

  return interaction;
};

const createInteractionString = (field: HTMLElement): string | undefined => {
  if (isInput(field)) {
    return createInteractionStringFromInput(field);
  }

  if (isSelect(field)) {
    return createInteractionStringFromSelect(field);
  }
};

const createInteractionStringFromInput = (
  field: HTMLInputElement,
): string | undefined => {
  const name = getFieldName(field);

  if (typeof name === 'undefined') {
    return undefined;
  }

  if (field.type !== 'checkbox' && field.type !== 'radio') {
    return name;
  }

  if (field.type === 'checkbox') {
    return [
      FormAttributionElement.Checkbox,
      normalizeTrackingValue(name),
      field.checked
        ? FormAttributionBoolean.True
        : FormAttributionBoolean.False,
    ].join('.');
  }

  if (field.type === 'radio') {
    return [
      FormAttributionElement.Radio,
      normalizeTrackingValue(name),
      normalizeTrackingValue(field.value),
    ].join('.');
  }

  return undefined;
};

const createInteractionStringFromSelect = (
  field: HTMLSelectElement,
): string | undefined => {
  const fieldName = getFieldName(field);

  if (typeof fieldName === 'undefined') {
    return undefined;
  }

  return [FormAttributionElement.Select, fieldName, field.value].join('.');
};

const isInput = (element: HTMLElement): element is HTMLInputElement => {
  return element.nodeName.toLowerCase() === 'input';
};

const isSelect = (element: HTMLElement): element is HTMLSelectElement => {
  return element.nodeName.toLowerCase() === 'select';
};
